import { StatusBadge, type Tone } from "@/components/ui/StatusBadge";
import { DISMISS_REASONS } from "@/api/discrepancies";
import { formatUsd } from "@/utils/ezyvetModels";
import type { Discrepancy } from "@/types/ezyvet";

const SEVERITY: Record<Discrepancy["severity"], { tone: Tone; label: string }> = {
  red: { tone: "unbilled", label: "Unbilled" },
  yellow: { tone: "discrepancy", label: "Discrepancy" },
  green: { tone: "synced", label: "Billed" },
};

interface Props {
  discrepancy: Discrepancy;
  busy: boolean;
  onPushTask: () => void;
  onDismiss: () => void;
  onMarkAdded: () => void;
}

/** Drawer beside the panes: what was flagged and what to do about it. Shortcuts are handled by the workbench. */
export function ActionPanel({ discrepancy: d, busy, onPushTask, onDismiss, onMarkAdded }: Props) {
  const severity = SEVERITY[d.severity];
  const open = d.status === "pending";

  return (
    <aside className="action-panel" aria-labelledby="action-title">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <h2 id="action-title" style={{ margin: 0 }}>
          {d.event_description}
        </h2>
        <StatusBadge tone={severity.tone}>{severity.label}</StatusBadge>
      </div>

      <dl className="kv kv--compact">
        <dt>Est. value</dt>
        <dd className="mono">{formatUsd(d.estimated_value_usd)}</dd>
        <dt>Confidence</dt>
        <dd className="mono">{Math.round(d.confidence * 100)}%</dd>
        <dt>Status</dt>
        <dd>
          <StatusBadge tone={open ? "discrepancy" : d.status === "dismissed" ? "neutral" : "synced"}>{d.status}</StatusBadge>
        </dd>
        {d.matched_invoice_item && (
          <>
            <dt>Closest line</dt>
            <dd>{d.matched_invoice_item}</dd>
          </>
        )}
        {d.dismiss_reason && (
          <>
            <dt>Dismissed as</dt>
            <dd>
              {DISMISS_REASONS[d.dismiss_reason]}
              {d.review_notes && <span className="muted"> · {d.review_notes}</span>}
            </dd>
          </>
        )}
        {d.pms_task_id && (
          <>
            <dt>ezyVet task</dt>
            <dd className="mono">#{d.pms_task_id}</dd>
          </>
        )}
      </dl>

      {d.recommended_action && <p className="callout info">{d.recommended_action}</p>}

      <div className="action-buttons">
        <button
          type="button"
          className="btn btn-primary"
          disabled={!open || busy || Boolean(d.pms_task_id) || d.external_source !== "ezyvet"}
          onClick={onPushTask}
          aria-keyshortcuts="t"
          title={
            d.pms_task_id
              ? "A task was already created for this flag"
              : d.external_source !== "ezyvet"
                ? "Only consults synced from ezyVet can have tasks pushed back"
                : undefined
          }
        >
          Push task to ezyVet <kbd>t</kbd>
        </button>
        <button type="button" className="btn" disabled={!open || busy} onClick={onMarkAdded} aria-keyshortcuts="a">
          Mark added to invoice <kbd>a</kbd>
        </button>
        <button type="button" className="btn btn-danger" disabled={!open || busy} onClick={onDismiss} aria-keyshortcuts="d">
          Dismiss discrepancy <kbd>d</kbd>
        </button>
      </div>
      {!open && <p className="muted" style={{ marginTop: 12 }}>Already {d.status}. Nothing left to do here.</p>}
    </aside>
  );
}
